import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Globe,
  Eye,
  Sparkles,
  ArrowRight,
  GraduationCap,
  Languages as LangIcon,
  Award,
  BookOpen,
  UserCog,
  Lock,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import Logo from "@/components/Logo";
import { Skeleton } from "@/components/Skeleton";
import OGTags from "@/components/OGTags";
import SecureConnectModal from "@/components/SecureConnectModal";

export default function PublicProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/profile/${username}`);
        setProfile(data);
      } catch { setNotFound(true); }
    })();
  }, [username]);

  if (notFound) {
    return (
      <Shell>
        <div className="tb-card p-10 text-center max-w-md mx-auto">
          <span className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-dashed border-border mb-3">
            <UserCog className="h-6 w-6 text-muted-foreground" />
          </span>
          <p className="font-display text-lg font-medium">Profile not found</p>
          <p className="mt-1 text-sm text-muted-foreground">This educator may have made their profile private.</p>
          <Button className="mt-4 rounded-full" onClick={() => navigate("/")}>Go to TeeBee</Button>
        </div>
      </Shell>
    );
  }

  if (!profile) {
    return (
      <Shell>
        <Skeleton className="h-64 max-w-3xl mx-auto" />
      </Shell>
    );
  }

  return (
    <Shell>
      <OGTags
        title={profile.display_name || "Educator on TeeBee"}
        description={profile.bio?.slice(0, 160) || `${profile.display_name} — educator on TeeBee · by Factskoot.`}
        image={`${window.location.origin}/api/og/profile/${username}`}
        type="profile"
      />
      {/* Hero */}
      <section className="max-w-4xl mx-auto mb-8 relative">
        <div className="absolute inset-0 tb-grid-bg opacity-50 pointer-events-none rounded-3xl" />
        <div className="relative grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-5 items-start">
          <div className="h-24 w-24 sm:h-32 sm:w-32 rounded-3xl overflow-hidden border border-border bg-muted grid place-items-center shrink-0">
            {profile.photo_url ? (
              <img src={profile.photo_url} alt={profile.display_name} className="h-full w-full object-cover" />
            ) : (
              <UserCog className="h-10 w-10 text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0">
            <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
              <Globe className="h-3 w-3" /> Public educator profile
            </span>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium leading-tight">
              {profile.display_name}
            </h1>
            <p className="mt-2 text-lg text-muted-foreground">{profile.headline}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
              {profile.institution_name && <span className="inline-flex items-center gap-1"><GraduationCap className="h-3 w-3" /> {profile.institution_name}</span>}
              <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {profile.profile_views} views</span>
              {profile.experience_years > 0 && <span>{profile.experience_years} yrs experience</span>}
            </div>

            {/* Secure Connect CTA — only show for OTHER users (not your own profile) */}
            {profile.user_id && (!user || user.user_id !== profile.user_id) && (
              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                <Button
                  onClick={() => user ? setConnectOpen(true) : navigate(`/login?return=/u/${username}`)}
                  className="rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-600 to-amber-500 hover:opacity-90 text-white shadow-md"
                  data-testid="profile-secure-connect"
                >
                  <Lock className="h-3.5 w-3.5 mr-1.5" />
                  Secure Connect · ₹199
                </Button>
                <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                  Private 7-day chat · only the two of you
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bio */}
      {profile.bio && (
        <section className="max-w-4xl mx-auto tb-card p-6 mb-5">
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{profile.bio}</p>
        </section>
      )}

      {/* Expertise */}
      <section className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <ChipsCard title="Subjects"     items={profile.subjects}    icon={BookOpen} />
        <ChipsCard title="Curriculums"  items={profile.curriculums} icon={GraduationCap} />
        <ChipsCard title="Languages"    items={profile.languages}   icon={LangIcon} />
      </section>

      {/* Achievements + Philosophy */}
      <section className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        {profile.achievements?.length > 0 && (
          <div className="tb-card p-6">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2 mb-3">
              <Award className="h-3 w-3" /> Achievements
            </p>
            <ul className="space-y-2 text-sm">
              {profile.achievements.map((a, i) => <li key={`${i}-${(a || '').slice(0, 24)}`}>· {a}</li>)}
            </ul>
          </div>
        )}
        {profile.teaching_philosophy && (
          <div className="tb-card p-6">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2 mb-3">
              <Sparkles className="h-3 w-3" /> Teaching philosophy
            </p>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{profile.teaching_philosophy}</p>
          </div>
        )}
      </section>

      {/* Published lessons */}
      {profile.published_lessons?.length > 0 && (
        <section className="max-w-4xl mx-auto mb-5">
          <h3 className="font-display text-xl font-medium tracking-tight mb-3">Public lessons</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {profile.published_lessons.map((l) => (
              <button
                key={l.share_token}
                onClick={() => navigate(`/l/${l.share_token}`)}
                className="tb-card-interactive p-4 text-left"
                data-testid={`profile-lesson-${l.share_token}`}
              >
                <p className="font-display text-base font-medium line-clamp-2">{l.title}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {[l.subject, l.grade && `Grade ${l.grade}`].filter(Boolean).join(" · ")}
                </p>
                <div className="mt-3 text-[11px] text-muted-foreground inline-flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {l.view_count || 0}</span>
                  <span>·</span>
                  <span>Open <ArrowRight className="h-3 w-3 inline" /></span>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Footer CTA */}
      <section className="max-w-4xl mx-auto tb-card-feature mt-6 text-center bg-gradient-to-br from-primary/5 via-card to-primary/5">
        <div className="mx-auto h-12 w-12 rounded-full tb-ai-orb grid place-items-center mb-3 tb-float">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <h3 className="font-display text-xl tracking-tight font-medium">Build your own educator identity</h3>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-md mx-auto">
          Join thousands of teachers using TeeBee to plan lessons, build content and grow their professional brand.
        </p>
        <div className="mt-4 flex flex-wrap gap-2 justify-center">
          {user ? (
            <Button className="rounded-full" onClick={() => navigate("/profile")}>
              <UserCog className="h-4 w-4 mr-2" /> Edit my profile
            </Button>
          ) : (
            <>
              <Button className="rounded-full" onClick={() => navigate("/signup")}>
                Sign up free <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => navigate("/login")}>
                I have an account
              </Button>
            </>
          )}
        </div>
      </section>

      {/* Secure Connect Modal */}
      <SecureConnectModal
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        recipientUserId={profile.user_id}
        recipientName={profile.display_name}
      />
    </Shell>
  );
}

function ChipsCard({ title, items = [], icon: Icon }) {
  if (!items.length) return null;
  return (
    <div className="tb-card p-5">
      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2 mb-2">
        <Icon className="h-3 w-3" /> {title}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((s, i) => (
          <span key={`${i}-${(s || '').slice(0, 24)}`} className="tb-chip-mint rounded-full px-2.5 py-1 text-[11px] font-medium">{s}</span>
        ))}
      </div>
    </div>
  );
}

function Shell({ children }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <div className="min-h-screen tb-auth-bg">
      <header className="sticky top-0 z-30 tb-glass border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2">
            <Logo size="sm" to={null} />
          </button>
          <div className="flex items-center gap-2">
            {user ? (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate("/dashboard")}>Dashboard</Button>
            ) : (
              <Button size="sm" className="rounded-full" onClick={() => navigate("/signup")}>Try TeeBee free</Button>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">{children}</main>
    </div>
  );
}
